import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  TrendingUp,
  Award,
  Calendar,
  BarChart,
  Zap,
  Globe,
  Upload,
  Clock,
  Edit,
} from 'lucide-react';
import { formatModernTime, formatDuration, getISTDate } from '@/lib/utils';
import { Value } from '@radix-ui/react-select';

interface DailyStats {
  uploads_today: number;
  total_uploads: number;
  last_upload_date: string;
}

interface ContributionItem {
  id: string;
  size: number;
  category_id: string;
  reviewed: boolean;
  title: string;
  description: string;
  duration?: number;
  timestamp?: string;
  location?: { latitude: number; longitude: number };
  release_rights: string;
  language: string;
  file_hash: string;
  snr_frequency: number;
}

interface UserContributions {
  totalContributions: number;
  contributionsByType: {
    text: number;
    audio: number;
    image: number;
    video: number;
    document: number;
  };
  audioContributions: ContributionItem[];
  videoContributions: ContributionItem[];
  textContributions: ContributionItem[];
  imageContributions: ContributionItem[];
  documentContributions: ContributionItem[];
  audioDuration: number;
  videoDuration: number;
}

interface ContributionDashboardProps {
  dailyStats: DailyStats | null;
  contributions: UserContributions | null;
  loading: boolean;
  edits: number;
  onMediaTypeClick?: (
    mediaType: 'text' | 'audio' | 'video' | 'image' | 'document',
  ) => void;
}

const ContributionDashboard: React.FC<ContributionDashboardProps> = ({
  dailyStats,
  contributions,
  loading,
  edits,
  onMediaTypeClick,
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">
          {t('messages.loadingContributionsDashboard')}
        </p>
      </div>
    );
  }

  if (!dailyStats && !contributions) {
    return (
      <div className="text-center text-gray-500 py-8">
        {t('common.noContributionDataAvailable')}
      </div>
    );
  }

  const getLanguageContributions = () => {
    const languageMap: { [key: string]: number } = {};
    const allContributions = [
      ...(contributions?.audioContributions || []),
      ...(contributions?.videoContributions || []),
      ...(contributions?.textContributions || []),
      ...(contributions?.imageContributions || []),
      ...(contributions?.documentContributions || []),
    ];

    allContributions.forEach((item) => {
      if (item.language) {
        languageMap[item.language] = (languageMap[item.language] || 0) + 1;
      }
    });

    return Object.entries(languageMap)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([lang, count]) => ({ language: lang, count }));
  };

  const languageContributions = getLanguageContributions();

  const getMonthlyContributions = () => {
    const monthlyMap: { [key: string]: number } = {};
    const allContributions = [
      ...(contributions?.audioContributions || []),
      ...(contributions?.videoContributions || []),
      ...(contributions?.textContributions || []),
      ...(contributions?.imageContributions || []),
      ...(contributions?.documentContributions || []),
    ];

    allContributions.forEach((item) => {
      if (item.timestamp) {
        const dateIST = getISTDate(item.timestamp); // Convert UTC timestamp to IST Date object
        const monthYear = dateIST.toLocaleString('en-US', {
          month: 'short',
          year: 'numeric',
          timeZone: 'Asia/Kolkata', // Explicitly set timezone to IST
        });
        monthlyMap[monthYear] = (monthlyMap[monthYear] || 0) + 1;
      }
    });

    // Sort by year and then month
    return Object.entries(monthlyMap)
      .sort(([monthYearA], [monthYearB]) => {
        const dateA = new Date(monthYearA);
        const dateB = new Date(monthYearB);
        return (
          dateA.getFullYear() - dateB.getFullYear() ||
          dateA.getMonth() - dateB.getMonth()
        );
      })
      .map(([month, count]) => ({ month, count }));
  };

  const monthlyContributions = getMonthlyContributions();

  const calculateUploadsToday = () => {
    if (!contributions) return 0;

    const allContributions = [
      ...(contributions.audioContributions || []),
      ...(contributions.videoContributions || []),
      ...(contributions.textContributions || []),
      ...(contributions.imageContributions || []),
      ...(contributions.documentContributions || []),
    ].filter((item) => item.timestamp);

    if (allContributions.length === 0) {
      return 0;
    }

    const todayIST = getISTDate(new Date().toISOString()); // Get current date in IST
    todayIST.setHours(0, 0, 0, 0); // Set to start of IST day

    let uploadsTodayCount = 0;
    allContributions.forEach((item) => {
      const itemDateIST = getISTDate(item.timestamp); // Convert UTC timestamp to IST Date object
      itemDateIST.setHours(0, 0, 0, 0); // Set to start of IST day
      if (itemDateIST.getTime() === todayIST.getTime()) {
        uploadsTodayCount++;
      }
    });
    return uploadsTodayCount;
  };

  const uploadsToday = calculateUploadsToday();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 5,
        }}
      >
        {[
          {
            label: 'Uploads',
            value: contributions?.totalContributions || 0,
            color: 'linear-gradient(135deg, #34d399, #10b981)',
            icon: <TrendingUp size={8} />,
          },
          {
            label: 'Edits',
            value: edits,
            color: '#aada00ff',
            icon: <Edit size={8} />,
          },
          {
            label: 'Hours',
            value: formatDuration(
              (contributions?.audioDuration || 0) +
                (contributions?.videoDuration || 0),
            ),
            color: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
            icon: <Clock size={8} />,
          },
          {
            label: 'Today',
            value: uploadsToday,
            color: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
            icon: <Upload size={8} />,
          },
        ].map((item, idx) => (
          <div
            key={idx}
            style={{
              background: item.color,
              borderRadius: 8,
              padding: '4px 8px',
              color: '#fff',
              textAlign: 'center',
              height: 38,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <div style={{ fontSize: '0.58rem', opacity: 0.9 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '0.58rem', opacity: 0.85 }}>
                  {item.icon}
                </div>
              </div>
              <div
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 'bold',
                  lineHeight: 1.1,
                }}
              >
                {item.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      <hr />

      {/* Contributions by Media Type */}
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 5,
          }}
        >
          {[
            {
              label: 'Text',
              value: contributions?.contributionsByType.text || 0,
              color: '#3b82f6',
              icon: <Activity size={8} />,
            },
            {
              label: 'Doc',
              value: contributions?.contributionsByType.document || 0,
              color: '#ef4444',
              icon: <BarChart size={8} />,
            },
            {
              label: 'Image',
              value: contributions?.contributionsByType.image || 0,
              color: '#f97316',
              icon: <Award size={8} />,
            },
            {
              label: 'Audio',
              value: contributions?.contributionsByType.audio || 0,
              color: '#22c55e',
              icon: <TrendingUp size={8} />,
            },
            {
              label: 'Video',
              value: contributions?.contributionsByType.video || 0,
              color: '#8b5cf6',
              icon: <Calendar size={8} />,
            },
          ].map((item, idx) => {
            const mediaTypeMap: {
              [key: string]: 'text' | 'audio' | 'video' | 'image' | 'document';
            } = {
              Text: 'text',
              Doc: 'document',
              Image: 'image',
              Audio: 'audio',
              Video: 'video',
            };

            const mediaType = mediaTypeMap[item.label];

            return (
              <div
                key={idx}
                onClick={
                  mediaType ? () => onMediaTypeClick?.(mediaType) : undefined
                }
                style={{
                  background: item.color,
                  borderRadius: 8,
                  padding: '4px 8px',
                  color: '#fff',
                  textAlign: 'center',
                  cursor: mediaType ? 'pointer' : 'default',
                  opacity: mediaType ? 1 : 0.6,
                  height: 38,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                }}
                className={
                  mediaType ? 'hover:brightness-110 transition-all' : ''
                }
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                    }}
                  >
                    <div style={{ fontSize: '0.58rem', opacity: 0.9 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '0.58rem', opacity: 0.85 }}>
                      {item.icon}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: '0.82rem',
                      fontWeight: 'bold',
                      lineHeight: 1.1,
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface DashboardCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  unit?: string;
  color: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  icon,
  title,
  value,
  unit,
  color,
}) => (
  <div
    className={`p-5 rounded-lg shadow-sm flex items-center space-x-4 ${color}`}
  >
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className="text-2xl font-bold text-gray-900">
        {value}
        {unit && <span className="text-base font-normal ml-1">{unit}</span>}
      </p>
    </div>
  </div>
);

interface MediaTypeCardProps {
  type: string;
  count: number;
  duration?: number;
  icon: React.ReactNode;
  color: string;
}

const MediaTypeCard: React.FC<MediaTypeCardProps> = ({
  type,
  count,
  duration,
  icon,
  color,
}) => (
  <div className={`p-4 rounded-lg shadow-sm text-center ${color}`}>
    <div className="flex justify-center mb-2">{icon}</div>
    <p className="text-xl font-bold text-gray-900">{count}</p>
    <p className="text-sm text-gray-600">{type} Contributions</p>
    {duration !== undefined && duration > 0 && (
      <p className="text-xs text-gray-500 mt-1">
        {formatDuration(duration)} total
      </p>
    )}
  </div>
);

export default ContributionDashboard;
